// @givmo/board — a person's two facts on the roster, read once and said the same way everywhere
// (CBG-YR3-ROLES layer 1, D-CBG-21; backend contract `board-officer-roles.md`).
//
//   * DIRECTOR, yes or no (`is_director`) — the one fact that decides who votes, who counts toward
//     quorum and who signs a written consent. The pickers, the circulate preview and the roster's
//     count read it here and nowhere else (the derivation's G4 and G5).
//   * TITLES, zero or more (`titles[]`) — labels the organization chose: Chair, Vice Chair, Secretary
//     and Treasurer to start with, several per person, director or not. A title changes no vote,
//     quorum or consent, and nothing here reads one to decide anything.
//
// Neither fact authorizes anything: everyone on the roster gets the same records, and the board's
// meetings' packets and invitations (the backend's §5.2; a committee's go to its members,
// CBG-YR6-MEET). `board_role` is the LEGACY word the backend keeps writing through
// the expand window; this module reads it only for an answer that predates the director fact, and then
// as a POSITIVE test over the old voting words — never `board_role !== 'observer'`, which would admit
// any word a later build adds.
//
// ⚠ ONE HELPER, `capacityLine`, names a person's facts wherever a person is named (the derivation's §6
// item 13): it replaced the roster's `roleLabel` and the director directory's `seatLabel`. A written
// consent's snapshot is the exception, and keeps its own frozen word (`seatRoleLabel`): the snapshot
// carries the legacy word only, and everyone on it signs as a director.
//
// Decoded tolerantly: an older answer with neither field, a title row missing a field, or a title key
// or status this build doesn't know never throws — a malformed row is dropped, an unknown key is kept as
// a custom title would be, and an unknown status reads as not held.

import type {
  BoardMemberTitleRef,
  BoardOfficerTitleKey,
  BoardOfficerTitleOut,
} from '@givmo/api-client';
import { extractList } from '@givmo/api-client/list';
import { displayDate } from '../../lib/display';
import { officerRolesL2Words as l2 } from './officer-roles-l2-words';
import { officerRolesL3Words as l3 } from './officer-roles-l3-words';
import { officerRolesWords as w } from './officer-roles-words';

/** The legacy word's voting seats, for an answer that predates the director fact. */
const LEGACY_DIRECTOR_WORDS: ReadonlySet<string> = new Set(['chair', 'secretary', 'director']);

/** Any seat-shaped answer — the roster's `OrgBoardMemberOut`, or a roster-sourced picker's — read off
 *  `unknown` where it matters, so an answer missing a field never throws. */
export interface SeatFacts {
  board_role?: string | null;
  is_director?: unknown;
  titles?: unknown;
}

/**
 * Whether the person is a director: the served fact; for an answer without it, the legacy word's
 * positive set (every existing seat answers the same either way through the expand window).
 */
export function isDirector(seat: SeatFacts): boolean {
  if (typeof seat.is_director === 'boolean') return seat.is_director;
  return LEGACY_DIRECTOR_WORDS.has(String(seat.board_role ?? '').toLowerCase());
}

/** One title a person holds or held, as the seat's `titles[]` serves it. */
export interface SeatTitle {
  id: string;
  title_id: string;
  /** Which of Givmo's defaults this is (`chair`, `vice_chair`, `secretary`, `treasurer`), or null. */
  default_key: string | null;
  /** The title's CURRENT label, as the board names it. */
  label: string;
  /** `active` while held; anything else reads as no longer held. */
  status: string;
  /** `module`, `carried_over` or `imported` (a key this build doesn't know is kept as served). */
  provenance: string;
  held_from: string | null;
  held_to: string | null;
  assigned_at: string | null;
  ended_at: string | null;
}

function text(value: unknown): string | null {
  return typeof value === 'string' && value.trim().length > 0 ? value : null;
}

function decodeSeatTitle(value: unknown): SeatTitle | null {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
  const row = value as Record<string, unknown>;
  const titleId = text(row.title_id);
  const label = text(row.label);
  if (titleId === null || label === null) return null;
  return {
    id: text(row.id) ?? titleId,
    title_id: titleId,
    default_key: text(row.default_key),
    label: label.trim(),
    status: text(row.status) ?? '',
    provenance: text(row.provenance) ?? '',
    held_from: text(row.held_from),
    held_to: text(row.held_to),
    assigned_at: text(row.assigned_at),
    ended_at: text(row.ended_at),
  };
}

/** Every title the seat carries, active first in the board's order, then ended (as served). */
export function seatTitles(seat: SeatFacts): SeatTitle[] {
  const rows = Array.isArray(seat.titles) ? seat.titles : [];
  return rows.map(decodeSeatTitle).filter((title): title is SeatTitle => title !== null);
}

/** The titles the person holds now, in the board's order. */
export function currentTitles(seat: SeatFacts): SeatTitle[] {
  return seatTitles(seat).filter((title) => title.status === 'active');
}

/** The titles the person held before, newest first (as served) — layer 2's "Earlier titles". */
export function endedTitles(seat: SeatFacts): SeatTitle[] {
  return seatTitles(seat).filter((title) => title.status !== 'active');
}

/**
 * The organization's own dates for a title, as one short phrase (CBG-YR3-ROLES layer 2): "Since Oct
 * 2, 2024", "Feb 1, 2023 – Oct 1, 2024", "Until Oct 1, 2024", or null when it recorded neither. The
 * board's dates only — never Board's own instants, which say when someone told Board.
 */
export function titleDatesLine(title: SeatTitle): string | null {
  if (title.held_from !== null && title.held_to !== null)
    return l2.heldRange(displayDate(title.held_from), displayDate(title.held_to));
  if (title.held_from !== null) return l2.heldSince(displayDate(title.held_from));
  if (title.held_to !== null) return l2.heldUntil(displayDate(title.held_to));
  return null;
}

/** Words that stay on one line: no-break spaces, as `coi-decode.ts`'s `oneLine` keeps them (not
 *  imported here: that module imports this one), and a word joiner after a dash, which a line may
 *  otherwise break after even before a no-break space (UAX #14's LB12a). */
export const keptTogether = (words: string) =>
  words.replace(/ /g, '\u00a0').replace(/–/g, '–\u2060');

/**
 * A title's dates on the member's own page (CBG-YR3-ROLES-HOLDOVER-PAGE): a title still held past its
 * recorded term end says so — "Since Oct 2, 2024 · Term ended Jun 30, 2026 – still serving", or with
 * no start "Term ended Jun 30, 2026 – still serving" — and any other keeps `titleDatesLine`. The
 * held-over phrase stays on one line wherever the row wraps, so a phone breaks after the "·", never
 * inside it (the orchestrator's 23:26 PDT ruling). Shown only: nothing stores this line.
 */
export function memberTitleDatesLine(title: SeatTitle, today: string): string | null {
  const heldOver = heldOverSince(title, today);
  if (heldOver === null) return titleDatesLine(title);
  const ended = l3.heldOverDates(null, displayDate(heldOver));
  const line = l3.heldOverDates(
    title.held_from !== null ? displayDate(title.held_from) : null,
    displayDate(heldOver),
  );
  return line.replace(ended, keptTogether(ended));
}

/** "A", "A and B", "A, B and C". */
export function joinWords(words: readonly string[]): string {
  if (words.length <= 1) return words[0] ?? '';
  return `${words.slice(0, -1).join(', ')} ${w.and} ${words[words.length - 1]}`;
}

/**
 * The person's facts as one line, wherever a person is named: "Director · Chair", "Director",
 * "Secretary and Treasurer · not a director", "Not a director".
 */
export function capacityLine(seat: SeatFacts): string {
  return capacityLineOf(
    isDirector(seat),
    currentTitles(seat).map((title) => title.label),
  );
}

/**
 * The same line, in the same words, from the two facts alone: for a person a read describes rather
 * than the roster (the presiding dialog labels each person by the titles held on the MEETING's day,
 * CBG-YR3-ROLES-PRESIDING; review-138's F5).
 */
export function capacityLineOf(director: boolean, labels: readonly string[]): string {
  if (director)
    return labels.length > 0 ? w.directorWithTitles(joinWords(labels)) : w.directorAlone;
  return labels.length > 0 ? w.titlesNotDirector(joinWords(labels)) : w.notDirector;
}

/**
 * The person's facts on their OWN list of boards (the director directory's `is_director` and
 * `title_labels`): a director as `capacityLine` says it; someone who isn't a director by their titles
 * alone — their own list needn't say what they aren't — or, holding none, "Not a director".
 */
export function ownBoardCapacity(board: {
  is_director: boolean;
  title_labels: readonly string[];
}): string {
  const labels = board.title_labels;
  if (board.is_director)
    return labels.length > 0 ? w.directorWithTitles(joinWords(labels)) : w.directorAlone;
  // Drawn, l1-your-boards ("Secretary and Treasurer"); "Not a director" for someone who holds no
  // title is not drawn.
  return labels.length > 0 ? joinWords(labels) : w.notDirector;
}

/** The roster's Titles cell: "Secretary, Treasurer", or null for none. */
export function titlesCell(seat: SeatFacts): string | null {
  const labels = currentTitles(seat).map((title) => title.label);
  return labels.length > 0 ? labels.join(', ') : null;
}

/** Serving seats that are directors — whom a written consent goes to, and who votes. */
export function servingDirectors<Seat extends SeatFacts & { status?: string | null }>(
  seats: readonly Seat[],
): Seat[] {
  return seats.filter((seat) => (seat.status ?? '') === 'active' && isDirector(seat));
}

/** Serving seats that are not directors — named once where they have no part to play. */
export function servingNonDirectors<Seat extends SeatFacts & { status?: string | null }>(
  seats: readonly Seat[],
): Seat[] {
  return seats.filter((seat) => (seat.status ?? '') === 'active' && !isDirector(seat));
}

/**
 * The word a carried-over title came from: the seat word the migration read it off (`chair` or
 * `secretary`, its default key), so a title the board renames later still names the seat it came
 * from. Null for a title that wasn't carried over, or one with no default key.
 */
export function carriedOverSeatWord(title: SeatTitle): string | null {
  if (title.provenance !== 'carried_over' || title.default_key === null) return null;
  return title.default_key.replace(/_/g, ' ');
}

// ── The board's catalog (`GET …/officer-titles`) ─────────────────────────────────────────────

/** One title the dialogs offer: its stored id, or — for a default nobody holds yet — its key. */
export interface CatalogTitle {
  /** The stable choice key: the stored id, else `default:<key>`. */
  key: string;
  id: string | null;
  default_key: string | null;
  label: string;
}

/** The catalog's ACTIVE titles in the board's order, decoded tolerantly (a row without a label or
 *  either reference is dropped). */
export function extractOfficerTitles(body: unknown): CatalogTitle[] {
  return extractList<BoardOfficerTitleOut>(body, 'data')
    .filter((row) => typeof row === 'object' && row !== null)
    .filter((row) => (row.status ?? 'active') === 'active')
    .map((row) => {
      const id = text(row.id);
      const defaultKey = text(row.default_key);
      const label = text(row.label);
      if (label === null || (id === null && defaultKey === null)) return null;
      return {
        key: id ?? `default:${defaultKey}`,
        id,
        default_key: defaultKey,
        label: label.trim(),
      };
    })
    .filter((title): title is CatalogTitle => title !== null);
}

/**
 * The choices a dialog offers: the catalog, plus any title the seat holds that the catalog doesn't
 * list (a title the board has since retired), so a save — which REPLACES the seat's whole set of
 * titles — never ends one the person still holds without the officer seeing it.
 */
export function titleChoices(
  catalog: readonly CatalogTitle[],
  seat: SeatFacts | null,
): CatalogTitle[] {
  const choices = [...catalog];
  for (const held of seat === null ? [] : currentTitles(seat)) {
    if (!choices.some((choice) => matchesHeld(choice, held))) {
      choices.push({
        key: held.title_id,
        id: held.title_id,
        default_key: held.default_key,
        label: held.label,
      });
    }
  }
  return choices;
}

function matchesHeld(choice: CatalogTitle, held: SeatTitle): boolean {
  if (choice.id !== null) return choice.id === held.title_id;
  return choice.default_key !== null && choice.default_key === held.default_key;
}

/** The choice keys a seat holds now. */
export function heldChoiceKeys(choices: readonly CatalogTitle[], seat: SeatFacts | null): string[] {
  const held = seat === null ? [] : currentTitles(seat);
  return choices
    .filter((choice) => held.some((title) => matchesHeld(choice, title)))
    .map((choice) => choice.key);
}

/** One title's two dates as a dialog edits them: `YYYY-MM-DD`, or '' for none. */
export interface TitleDates {
  from: string;
  to: string;
}

/** The dates the seat's held titles carry now, by choice key (layer 2's dependent rows). */
export function heldTitleDates(
  choices: readonly CatalogTitle[],
  seat: SeatFacts | null,
): Record<string, TitleDates> {
  const dates: Record<string, TitleDates> = {};
  for (const title of seat === null ? [] : currentTitles(seat)) {
    const choice = choices.find((candidate) => matchesHeld(candidate, title));
    if (choice !== undefined)
      dates[choice.key] = { from: title.held_from ?? '', to: title.held_to ?? '' };
  }
  return dates;
}

/**
 * The write's `titles`: each ticked title by its stored id, or a default by its key — and, when the
 * dialog edits dates (layer 2), both of the title's dates, `null` for a blank one. The backend reads
 * them PATCH-shaped (an absent key keeps the stored date, `null` clears it), so sending both says
 * exactly what the dialog shows.
 */
export function titleRefs(
  choices: readonly CatalogTitle[],
  keys: readonly string[],
  dates?: Readonly<Record<string, TitleDates>>,
): BoardMemberTitleRef[] {
  return choices
    .filter((choice) => keys.includes(choice.key))
    .map((choice) => {
      const ref: BoardMemberTitleRef =
        choice.id !== null
          ? { title_id: choice.id }
          : { default_key: choice.default_key as BoardOfficerTitleKey };
      if (dates === undefined) return ref;
      const held = dates[choice.key];
      return {
        ...ref,
        held_from: held !== undefined && held.from.trim() !== '' ? held.from.trim() : null,
        held_to: held !== undefined && held.to.trim() !== '' ? held.to.trim() : null,
      };
    });
}

// ── The Officers part (CBG-YR3-ROLES layer 2) ─────────────────────────────────────────────────

/** Givmo's starting labels by default key — the backend's class defaults, the same four for every
 *  organization today (D-COB-14) — so a row can say a default was renamed ("Givmo’s “Chair”,
 *  renamed") with no extra field (the mockups' W-2). */
export const DEFAULT_TITLE_LABELS: Readonly<Record<string, string>> = {
  chair: 'Chair',
  vice_chair: 'Vice Chair',
  secretary: 'Secretary',
  treasurer: 'Treasurer',
};

/** One row of the board's catalog as the Officers part shows it, active or retired. */
export interface OfficerCatalogRow {
  /** The stored id, or null for a default nobody has used yet (it is named by its key). */
  id: string | null;
  default_key: string | null;
  /** What the Officers writes name it by: the stored id, else the default's key. */
  ref: string;
  label: string;
  position: number;
  retired: boolean;
  retired_at: string | null;
}

/** The whole catalog in the board's order, retired titles included, decoded tolerantly: a row with
 *  no label or no reference is dropped, and a status this build doesn't know reads as active. */
export function extractOfficerCatalog(body: unknown): OfficerCatalogRow[] {
  return extractList<BoardOfficerTitleOut>(body, 'data')
    .filter((row) => typeof row === 'object' && row !== null)
    .map((row, index) => {
      const id = text(row.id);
      const defaultKey = text(row.default_key);
      const label = text(row.label);
      if (label === null || (id === null && defaultKey === null)) return null;
      return {
        id,
        default_key: defaultKey,
        ref: id ?? (defaultKey as string),
        label: label.trim(),
        position: typeof row.position === 'number' ? row.position : index + 1,
        retired: row.status === 'retired',
        retired_at: text(row.retired_at),
      };
    })
    .filter((row): row is OfficerCatalogRow => row !== null)
    .sort((a, b) => a.position - b.position || a.label.localeCompare(b.label));
}

/** The line under a row's name: renamed from Givmo's default, the board's own, or nothing. */
export function catalogRowNote(row: OfficerCatalogRow): string | null {
  if (row.default_key === null) return l2.customTitle;
  const start = DEFAULT_TITLE_LABELS[row.default_key];
  return start !== undefined && start !== row.label ? l2.renamedFrom(start) : null;
}

/** Who holds a title now: the names of the serving seats whose current titles include it. */
export function titleHolders<
  Seat extends SeatFacts & { status?: string | null; display_name?: string },
>(row: OfficerCatalogRow, seats: readonly Seat[]): string[] {
  return titleHolderRows(row, seats, '').map((holder) => holder.name);
}

// ── The holdover (CBG-YR3-ROLES-HOLDOVER, backend `board-officer-roles.md` §15) ───────────────────

const CALENDAR_DATE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * The board's recorded term end of a title still held, once that end has passed — a holdover: the
 * title stays in force until an Owner ends it or changes its dates (bylaws' "until their successors
 * are elected"; Jonathan, 2026-10-12). `today` is the reader's calendar day (`todayAsDateInput`).
 * Null for a title no longer held, one with no end recorded, or an end today or later. Compared as
 * calendar dates, never instants, so no zone moves the day.
 */
export function heldOverSince(title: SeatTitle, today: string): string | null {
  if (title.status !== 'active' || title.held_to === null) return null;
  if (!CALENDAR_DATE.test(title.held_to) || !CALENDAR_DATE.test(today)) return null;
  return title.held_to < today ? title.held_to : null;
}

/**
 * A seat's recorded term end when it is before `day` (CBG-YR3-VOTENOTE, backend `board_minutes.md`
 * §V8): the vote dialog's note against the meeting's day, the circulate preview's against the
 * consent's date (the consent page's own rule, `term_had_ended_on`), "Who was there"'s against the
 * meeting's day and today. Null with no end recorded, no day to test, or an end on the day or after.
 * Compared as calendar dates, never instants, so no zone moves the day. A fact the board recorded and
 * never a bar: nothing reads it to refuse or disable anything.
 */
export function termEndedBefore(
  termEndOn: string | null | undefined,
  day: string | null | undefined,
): string | null {
  const end = (termEndOn ?? '').trim();
  const on = (day ?? '').trim();
  if (!CALENDAR_DATE.test(end) || !CALENDAR_DATE.test(on)) return null;
  return end < on ? end : null;
}

/** One person holding a title now, as the Officers part names them. */
export interface TitleHolder {
  name: string;
  /** Their recorded term end for this title, when it has passed and the title is still theirs. */
  heldOverSince: string | null;
}

/** Who holds a title now — the serving seats whose current titles include it — each with the date a
 *  passed recorded term end says, so the Officers part can say "Term ended … – still serving". */
export function titleHolderRows<
  Seat extends SeatFacts & { status?: string | null; display_name?: string },
>(row: OfficerCatalogRow, seats: readonly Seat[], today: string): TitleHolder[] {
  const holders: TitleHolder[] = [];
  for (const seat of seats) {
    if ((seat.status ?? '') !== 'active') continue;
    const held = currentTitles(seat).find((title) =>
      row.id !== null
        ? title.title_id === row.id
        : row.default_key !== null && title.default_key === row.default_key,
    );
    const name = (seat.display_name ?? '').trim();
    if (held === undefined || name.length === 0) continue;
    holders.push({ name, heldOverSince: heldOverSince(held, today) });
  }
  return holders;
}

// ── "Signing as": the capacity a minutes signature is given in (layer 2) ──────────────────────────

/** "Signing as"'s choice for no title. A title id is a UUID, so it never collides with one. */
export const WITHOUT_TITLE = 'without-title';

/** A title a signature may name: one the signing seat holds NOW (`l2-director-sign`, `l2-onbehalf`). */
export interface CapacityChoice {
  title_id: string;
  label: string;
}

/**
 * "Signing as"'s options: the titles the seat holds now, after the choice that takes a chosen one
 * back off. The field starts EMPTY, even for someone with one title, until the board adopts duties
 * (question 9); with them, a host starts it on the title the duties suggest (layer 3), which the
 * options always include while the seat holds it.
 */
export function capacityOptions(titles: readonly CapacityChoice[]) {
  return [
    { value: WITHOUT_TITLE, label: l2.withoutTitle },
    ...titles.map((title) => ({ value: title.title_id, label: title.label })),
  ];
}

/**
 * The capacity a signature sends: the chosen title's id while the seat still holds it, else none —
 * the take-back choice, nothing chosen, or a title the seat no longer holds (the backend would
 * refuse that one, `board_signature_capacity_not_held`).
 */
export function chosenCapacity(
  value: string | null,
  titles: readonly CapacityChoice[],
): string | null {
  return value !== null && titles.some((title) => title.title_id === value) ? value : null;
}

/** What "Signing as" shows: the chosen title while held, the take-back choice, else its placeholder. */
export function capacityFieldValue(
  value: string | null,
  titles: readonly CapacityChoice[],
): string | null {
  return value === WITHOUT_TITLE ? WITHOUT_TITLE : chosenCapacity(value, titles);
}
